import { ChangeEvent, Dispatch, SetStateAction, useState } from 'react';

import { Box, Button } from '@mui/material';

import TextField from '@/components/TextField';
import Typography from '@/components/Typography';
import { spacing } from '@/constants/ui';
import StepButtons from '@/pages/Onboarding/OnboardingForm/Steps/StepButtons';
import { InviteRecipient } from '@/pages/Onboarding/types/recipient.types';
import { generateSecureRandomString } from '@/utils/generateSecureRandomString';
import { emailValidator } from '@/utils/validators';

const Invite = ({
  activeStep,
  setActiveStep,
  inviteRecipients,
  setInviteRecipients
}: {
  inviteRecipients: InviteRecipient[];
  setInviteRecipients: Dispatch<SetStateAction<InviteRecipient[]>>;
  activeStep: number;
  setActiveStep: Dispatch<SetStateAction<number>>;
}) => {
  const [emailErrors, setEmailErrors] = useState<Map<string, string>>(new Map());

  const validateEmail = () => {
    const errors: Map<string, string> = new Map();
    for (const recipient of inviteRecipients) {
      const { email, id } = recipient;
      if (email.length) {
        const errorMessage = emailValidator(email);
        if (errorMessage) {
          errors.set(id, errorMessage);
        }
      }
    }
    setEmailErrors(errors);
    return !errors
      .values()
      .filter(email => email?.length > 0)
      .toArray().length;
  };

  const handleRecipientChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    emailInput: InviteRecipient
  ) => {
    const errors = new Map(emailErrors);
    let temp: InviteRecipient[] = inviteRecipients.map(inviteRecipient => {
      if (inviteRecipient.id === emailInput.id) {
        errors.delete(inviteRecipient.id);
        return { email: e.target.value, id: inviteRecipient.id };
      }
      return inviteRecipient;
    });

    temp = temp.filter(rec => rec.email.length);

    if (temp.filter(rec => rec.email.length).length === temp.length) {
      temp = [...temp, { email: '', id: generateSecureRandomString() }];
    }
    setInviteRecipients(temp);
    setEmailErrors(errors);
  };
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing['1.5']
      }}
    >
      <Typography
        size={24}
        sx={{ fontWeight: 'bold' }}
      >
        Data is more valuable when it’s shared!
      </Typography>
      <Typography
        size={20}
        sx={{ fontWeight: 'bold' }}
      >
        Invite your teammates to collaborate...
      </Typography>

      {inviteRecipients.map(emailInput => {
        return (
          <TextField
            key={emailInput.id}
            autoComplete={`inviteEmail${emailInput.id}`}
            error={!!emailErrors.get(emailInput.id)}
            helperText={emailErrors.get(emailInput.id)}
            id={`inviteEmail${emailInput.id}`}
            label='Work email'
            name='inviteEmail'
            sx={{
              minWidth: 220
            }}
            value={emailInput.email}
            variant='filled'
            onChange={e => {
              handleRecipientChange(e, emailInput);
            }}
          />
        );
      })}

      <StepButtons
        activeStep={activeStep}
        nextDisabled={!inviteRecipients.filter(recipient => recipient.email?.length > 0).length}
        onBackClick={() => {
          setActiveStep(activeStep - 1);
        }}
        onNextClick={() => {
          if (validateEmail()) setActiveStep(activeStep + 1);
        }}
      />
      <Button
        fullWidth
        disabled={activeStep === 0}
        sx={{
          '&.MuiButton-outlined': {
            color: 'primary.main',
            borderColor: 'primary.main'
          },
          fontSize: 16,
          fontWeight: 600
        }}
        variant='outlined'
        onClick={() => {
          setActiveStep(activeStep + 1);
        }}
      >
        Skip for now
      </Button>
    </Box>
  );
};
export default Invite;
